import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { getMyEnrollments } from '../../../../services/enrollmentService';
import type { IEnrollment } from '../../../../types/enrollment';
import type { ICourse } from '../../../../types/course';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { BookCopy } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // ✅ 1. Import useNavigate
import { io } from 'socket.io-client';

const MyCourses: React.FC = () => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [enrollments, setEnrollments] = useState<IEnrollment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate(); // ✅ 2. Initialize useNavigate

    // ✅ This component no longer needs state for modals

    const fetchMyCourses = async () => {
        setIsLoading(true);
        try {
            const data = await getMyEnrollments();
            setEnrollments(data);
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthLoading && user && user.role === 'student') {
            fetchMyCourses();
        } else if (!isAuthLoading) {
            setIsLoading(false);
        }
    }, [isAuthLoading, user]);

    // Socket.IO logic for real-time updates remains the same
    useEffect(() => {
        if (!user) return;
        const socket = io(import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000', {
            withCredentials: true,
        });

        socket.on('connect', () => console.log('✅ Socket.IO connected for MyCourses'));
        socket.on('disconnect', () => console.log('❌ Socket.IO disconnected for MyCourses'));

        // ... (your socket.on event listeners)

        return () => {
            socket.disconnect();
        };
    }, [user]);
    
    // ✅ 3. Update the handler to navigate to the new page
    const handleViewModules = (courseId: string) => {
        navigate(`/dashboard/student/my-courses/${courseId}`);
    };

    if (isAuthLoading || isLoading) {
        return <Loader />;
    }
    
    const getProgressValue = (status: string) => {
        if (status === 'completed') return 100;
        // A new enrollment starts at 0% progress
        if (status === 'in-progress') return 0;
        return 0;
    };

    console.log("Enrollments Data:", enrollments);


    return (
        <>
            <Helmet>
                <title>My Courses | SGC Dashboard</title>
            </Helmet>
            <div className="space-y-6">
                <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <h1 className="text-3xl font-bold text-gray-800">My Learning</h1>
                    <p className="text-gray-500 mt-1">Your enrolled courses and learning materials.</p>
                </header>

                {enrollments.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
                        <BookCopy className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-lg font-medium text-gray-900">No Courses Found</h3>
                        <p className="mt-1 text-sm text-gray-500">You haven't enrolled in any courses yet. Explore our courses to get started!</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {enrollments.map(enrollment => {
                            if (!enrollment.course || typeof enrollment.course !== 'object') {
                                return null;
                            }
                            
                            const course = enrollment.course as ICourse;
                            const progress = getProgressValue(enrollment.completionStatus);
                            
                            return (
                                <div key={enrollment._id} className="bg-white rounded-2xl shadow-sm border flex flex-col">
                                    <div className="p-6 flex-grow">
                                        <h2 className="text-xl font-bold text-gray-800">{course.title}</h2>
                                        <p className="text-sm text-gray-600 mt-2 flex-grow">{course.description}</p>
                                    </div>
                                    <div className="px-6 pb-6">
                                        <div className="flex justify-between items-center mb-2 text-sm font-medium text-gray-600">
                                            <span>Progress</span>
                                            <span>{progress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                                        </div>
                                        <button 
                                            // ✅ 4. Call the new navigation handler with the course ID
                                            onClick={() => handleViewModules(course._id)}
                                            className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
                                        >
                                            View Modules
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {/* ✅ 5. The modal and suspense logic is no longer needed in this file */}
        </>
    );
};

export default MyCourses;